import { useQuery } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { IntegrationGrant } from '../../../../../../typings/api/skymap/rest/internal/project';
import { SkyMapAxiosServiceFactory } from '../../../../services/axios/skymap-axios-service-factory';
import { formattedDateTime } from '../../../../utils/dateUtils';
import { useDialog } from '../../../hooks/use-dialog';
import { AppContext } from '../../../state/app-state';
import { ProjectContext } from '../../../state/project-state/project-state';
import { Button } from '../../button/button';
import { ConfirmationDialog } from '../../confirmation-dialog/confirmation-dialog';
import { InfoItem } from './style';

interface Props {
  integration: IntegrationGrant['integration'];
}

const integrationGrantsQueryKey = (projectId: string) => ['getIntegrationGrants', projectId];

/** Who connected the integration and when, with a Revoke button. Project admins only. */
const IntegrationGrants = (props: Props) => {
  const { t } = useTranslation('integrations');
  const { project } = React.useContext(ProjectContext);
  const { isAdminForCompany } = React.useContext(AppContext);
  const revokeDialog = useDialog();
  const [grantToRevoke, setGrantToRevoke] = React.useState<IntegrationGrant>();
  const isAdmin = isAdminForCompany(project.companyId);

  const { data: grants, refetch } = useQuery({
    queryKey: integrationGrantsQueryKey(project.id),
    enabled: isAdmin && !!project.id,
    queryFn: async () => {
      const response = await SkyMapAxiosServiceFactory.instance
        .createProjectServiceInternal()
        .getIntegrationGrants({ path: { projectId: project.id } });
      return response.data;
    },
    networkMode: 'always',
    retry: false,
    refetchOnWindowFocus: false,
  });

  const liveGrants = (grants ?? []).filter(
    (grant) => grant.integration === props.integration && grant.revokedAt === null,
  );

  if (!isAdmin || liveGrants.length === 0) {
    return null;
  }

  return (
    <>
      {liveGrants.map((grant) => (
        <InfoItem key={grant.id}>
          <Row>
            <span className="notranslate">
              {t('provider.common.connectedBy', {
                ns: 'integrations',
                name: `${grant.grantedBy.firstName} ${grant.grantedBy.lastName}`,
                date: formattedDateTime(grant.grantedAt),
              })}
            </span>
            <Button
              color="primary"
              variant="contained"
              onClick={() => {
                setGrantToRevoke(grant);
                revokeDialog.show();
              }}
            >
              {t('provider.common.revoke', { ns: 'integrations' })}
            </Button>
          </Row>
        </InfoItem>
      ))}

      {revokeDialog.render(
        <ConfirmationDialog
          confirmationText={t('provider.common.revoke', { ns: 'integrations' })}
          negative={true}
          title={t('provider.common.revokeTitle', { ns: 'integrations' })}
          onCancel={() => revokeDialog.hide()}
          onConfirm={async () => {
            if (!grantToRevoke) {
              return;
            }
            try {
              await SkyMapAxiosServiceFactory.instance
                .createProjectServiceInternal()
                .revokeIntegrationGrant({
                  path: { projectId: project.id, grantId: grantToRevoke.id },
                });
            } catch (error) {
              // Another admin already revoked it: refresh instead of showing an error.
              if (!isAxiosError(error) || error.response?.status !== 409) {
                throw error;
              }
            }
            const refetchResult = await refetch();
            if (refetchResult.isError) {
              throw refetchResult.error;
            }
            revokeDialog.hide();
          }}
        >
          {t('provider.common.revokeQuestion', { ns: 'integrations' })}
        </ConfirmationDialog>,
      )}
    </>
  );
};

const Row = styled.div`
  display: flex;
  align-items: center;
  gap: 1em;
`;

export { IntegrationGrants };
